import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    Box, Typography, Button, Chip, Stack, IconButton, Collapse,
    CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShipmentSearchResults from './ShipmentSearchResults';
import ComparisonView from './ComparisonView';
import ShipmentSearchTool from '../ShipmentSearchTool';
import { ShipmentHistoryButton } from '../Checkout';
import { isOlderThan7Days } from '../../claim-helpers-shared';

const UNZUORDENBAR_KDNR = 15286;

const CopyField = ({ label, value }) => {
    if (!value) return null;
    return (
        <Box sx={{ cursor: 'copy' }} onClick={() => navigator.clipboard.writeText(String(value))}>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#999' }}>{label}</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>{value}</Typography>
        </Box>
    );
};

const DetailField = ({ label, value }) => (
    <Box>
        <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#999' }}>{label}</Typography>
        <Typography variant="body2" sx={{ color: value ? '#1a1a1a' : '#ccc' }}>{value || '–'}</Typography>
    </Box>
);

const STATUS = {
    OFFEN:       { label: 'Offen',     dot: '#f59e0b' },
    FREIGEGEBEN: { label: 'Gefunden',  dot: '#22c55e' },
    ABGELEHNT:   { label: 'Abgelehnt', dot: '#ef4444' },
    STORNIERT:   { label: 'Storniert', dot: '#94a3b8' },
};

const fmtDate = (iso) => {
    if (!iso) return '–';
    const d = new Date(iso);
    return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })
        + ' ' + d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
};

const ClaimCard = ({ claim, posConfig, sendMessage, cookies, registerMessageHandler, unregisterMessageHandler }) => {
    const [localStatus, setLocalStatus] = useState(claim.status);
    const [searchResults, setSearchResults] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searching, setSearching] = useState(false);
    const [actionLoading, setActionLoading] = useState(null);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);
    const shipmentResultsRef = useRef(null);
    const [rechercheOpen, setRechercheOpen] = useState(false);
    const [rechercheResult, setRechercheResult] = useState(null);
    const [rechercheConfirmOpen, setRechercheConfirmOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);

    const isOffen = localStatus === 'OFFEN';
    const canReject = isOffen && isOlderThan7Days(claim.erstelltAm);
    const sc = STATUS[localStatus] || STATUS.STORNIERT;

    const handleMessage = useCallback((msg) => {
        // Only process messages for this claim
        if (msg.claimId && msg.claimId !== claim.claimId) return;

        if (msg.task === 'respNotFoundClaim') {
            setActionLoading(null);
            if (msg.status === 'succeeded') {
                setSuccessMsg(`Nicht gefunden (${msg.nichtGefundenZaehler}×)`);
                setError(null);
            } else { setError(msg.message || 'Fehler'); }
        }
        if (msg.task === 'respRejectClaim') {
            setActionLoading(null);
            if (msg.status === 'succeeded') {
                setLocalStatus('ABGELEHNT'); setSuccessMsg('Abgelehnt.'); setSearchResults(null); setError(null);
            } else { setError(msg.message || 'Fehler'); }
        }
        if (msg.task === 'respSearchClaimShipments') {
            setSearching(false);
            if (msg.status === 'succeeded') {
                setSearchResults(msg.shipments || []); setError(null); setSearchOpen(true);
            } else { setError(msg.message || 'Fehler'); }
        }
        if (msg.task === 'respApproveClaim') {
            if (msg.status === 'succeeded') {
                setLocalStatus('FREIGEGEBEN'); setSearchResults(null); setSearchOpen(false);
                setRechercheOpen(false); setRechercheResult(null); setRechercheConfirmOpen(false);
                setSuccessMsg('Freigegeben.');
            }
            if (shipmentResultsRef.current) { shipmentResultsRef.current.handleMessage(msg); }
        }
    }, [claim.claimId]);

    useEffect(() => {
        if (registerMessageHandler) registerMessageHandler(handleMessage);
        return () => { if (unregisterMessageHandler) unregisterMessageHandler(); };
    }, [handleMessage, registerMessageHandler, unregisterMessageHandler]);

    const send = (task, extra = {}) => {
        setError(null); setSuccessMsg(null);
        sendMessage(JSON.stringify({ task, station: cookies.station, staff: cookies.staff, ...extra }));
    };

    const handleRechercheSelect = useCallback((s) => setRechercheResult(s), []);
    const isImLager = rechercheResult && String(rechercheResult.status) === '2';
    const isRechFremd = rechercheResult && rechercheResult.kdnr !== UNZUORDENBAR_KDNR;
    const executeRechercheApprove = () => {
        setRechercheConfirmOpen(false); setActionLoading('rechercheApprove');
        send('approveClaim', { claimId: claim.claimId, shipmentId: rechercheResult.id });
    };

    return (
        <>
            {/* ── Claim Row ── */}
            <Box sx={{
                bgcolor: '#fff', borderRadius: '10px', border: '1px solid #e5e5e3',
                overflow: 'hidden', opacity: isOffen ? 1 : 0.55, transition: 'opacity 0.2s',
            }}>
                {/* Main row */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.5,
                }}>
                    {/* Status dot */}
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: sc.dot, flexShrink: 0 }} />

                    {/* Claim info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a' }}>
                                {claim.kdnr} – {claim.kundenName || '–'}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: '#999' }}>{fmtDate(claim.erstelltAm)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                            <Typography sx={{ fontSize: 12, color: '#777' }}>
                                {claim.absender} · {(claim.suchbegriffe || []).join(', ')}
                                {claim.kommentar && <> · <em>{claim.kommentar}</em></>}
                            </Typography>
                            {(claim.nichtGefundenZaehler ?? 0) > 0 && (
                                <Chip label={`${claim.nichtGefundenZaehler}× n.g.`} size="small"
                                    sx={{ height: 18, fontSize: 10, bgcolor: '#fef3c7', color: '#92400e' }} />
                            )}
                            {localStatus === 'FREIGEGEBEN' && claim.zugeordneteSendung && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Chip label={`→ ${claim.zugeordneteSendung}`} size="small" color="success"
                                        sx={{ height: 18, fontSize: 10 }} />
                                    <ShipmentHistoryButton id={claim.zugeordneteSendung} />
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Actions — open claims */}
                    {isOffen && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                            <Button size="small" variant="outlined" disableElevation
                                sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#ddd', color: '#1a1a1a' }}
                                startIcon={searching ? <CircularProgress size={14} /> : <SearchIcon sx={{ fontSize: 16 }} />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (searchResults) { setSearchOpen(true); }
                                    else { setSearching(true); send('searchClaimShipments', { suchbegriffe: claim.suchbegriffe, claimId: claim.claimId }); }
                                }}
                                disabled={searching || actionLoading !== null}>
                                Übereinstimmungen
                            </Button>
                            <Button size="small" variant="outlined" disableElevation
                                sx={{ borderRadius: 2, textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#ddd', color: '#555' }}
                                startIcon={<ManageSearchIcon sx={{ fontSize: 16 }} />}
                                onClick={(e) => { e.stopPropagation(); setRechercheOpen(true); setRechercheResult(null); }}
                                disabled={actionLoading !== null}>
                                Recherche
                            </Button>
                            <Button size="small" variant="text"
                                sx={{ textTransform: 'none', fontSize: 11, color: '#999', minWidth: 0 }}
                                startIcon={actionLoading === 'notFound' ? <CircularProgress size={12} /> : <VisibilityOffIcon sx={{ fontSize: 14 }} />}
                                onClick={(e) => { e.stopPropagation(); setActionLoading('notFound'); send('notFoundClaim', { claimId: claim.claimId }); }}
                                disabled={actionLoading !== null}>
                                N.g.
                            </Button>
                            {canReject && (
                                <Button size="small" variant="text"
                                    sx={{ textTransform: 'none', fontSize: 11, color: '#d97706', minWidth: 0 }}
                                    startIcon={actionLoading === 'reject' ? <CircularProgress size={12} /> : <ThumbDownIcon sx={{ fontSize: 14 }} />}
                                    onClick={(e) => { e.stopPropagation(); setActionLoading('reject'); send('rejectClaim', { claimId: claim.claimId }); }}
                                    disabled={actionLoading !== null}>
                                    Ablehnen
                                </Button>
                            )}
                        </Stack>
                    )}

                    {/* Detail toggle — all cards */}
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDetailOpen(!detailOpen); }}
                        sx={{ ml: isOffen ? 0 : 'auto' }}>
                        {detailOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </Box>

                {/* Alerts */}
                {(error || successMsg) && (
                    <Box sx={{ px: 2.5, pb: 1 }}>
                        {error && <Alert severity="error" sx={{ borderRadius: 2, py: 0 }} onClose={() => setError(null)}>{error}</Alert>}
                        {successMsg && <Alert severity="success" sx={{ borderRadius: 2, py: 0 }} onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}
                    </Box>
                )}

                {/* Detail view — all cards */}
                <Collapse in={detailOpen}>
                    <Box sx={{ borderTop: '1px solid #e5e5e3', px: 2.5, py: 2 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#999', mb: 1.5 }}>
                            Suchauftrag-Details
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
                            <DetailField label="Claim-ID" value={claim.claimId} />
                            <DetailField label="Status" value={localStatus} />
                            <DetailField label="Kdnr" value={claim.kdnr} />
                            <DetailField label="Kunde" value={claim.kundenName} />
                            <DetailField label="Erstellt am" value={fmtDate(claim.erstelltAm)} />
                            <DetailField label="Geändert am" value={fmtDate(claim.geaendertAm)} />
                            <DetailField label="Erstellt von" value={claim.staffId || 'Kunde'} />
                            <DetailField label="Bearbeitet von" value={claim.bearbeitetVon} />
                            <DetailField label="Zugeordnete Sendung" value={claim.zugeordneteSendung} />
                        </Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#999', mt: 2, mb: 1 }}>
                            Formular-Angaben
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
                            <DetailField label="Absender" value={claim.absender} />
                            <DetailField label="Suchbegriffe" value={(claim.suchbegriffe || []).join(', ')} />
                            <DetailField label="Zustelldatum" value={claim.zustelldatum} />
                            <DetailField label="Spedition" value={claim.spedition} />
                            <DetailField label="Empfänger lt. Spedition" value={claim.empfaenger} />
                            <DetailField label="Nicht-gefunden-Zähler" value={claim.nichtGefundenZaehler} />
                        </Box>
                        {claim.kommentar && (
                            <Box sx={{ mt: 1.5 }}>
                                <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#999' }}>Kommentar</Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#555', mt: 0.25 }}>{claim.kommentar}</Typography>
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </Box>

            {/* ── Übereinstimmungen Modal ── */}
            <Dialog open={searchOpen} onClose={() => setSearchOpen(false)} maxWidth="lg" fullWidth
                PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>
                    Übereinstimmungen – {claim.kdnr} {claim.kundenName || ''}
                </DialogTitle>
                <DialogContent sx={{ minHeight: 300 }}>
                    {searching ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
                    ) : (
                        <ShipmentSearchResults
                            ref={shipmentResultsRef}
                            shipments={searchResults}
                            claim={claim}
                            sendMessage={sendMessage}
                            cookies={cookies}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setSearchOpen(false)} sx={{ textTransform: 'none', fontSize: 13 }}>Schliessen</Button>
                </DialogActions>
            </Dialog>

            {/* ── Recherche Modal ── */}
            <Dialog open={rechercheOpen} onClose={() => setRechercheOpen(false)} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>Sendung recherchieren</DialogTitle>
                <DialogContent sx={{ minHeight: 400 }}>
                    {!rechercheResult ? (
                        <Box>
                            {/* Alle Claim-Daten als Referenz */}
                            <Box sx={{ mb: 2, p: 2, bgcolor: '#f8f7f4', borderRadius: 2, border: '1px solid #e5e5e3' }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#999', mb: 1 }}>
                                    Suchauftrag-Daten (klicken zum Kopieren)
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
                                    <CopyField label="Kdnr" value={claim.kdnr} />
                                    <CopyField label="Kunde" value={claim.kundenName} />
                                    <CopyField label="Absender" value={claim.absender} />
                                    <CopyField label="Spedition" value={claim.spedition} />
                                    <CopyField label="Zustelldatum" value={claim.zustelldatum} />
                                    <CopyField label="Empfänger lt. Spedition" value={claim.empfaenger} />
                                </Box>
                                {claim.kommentar && (
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#999' }}>Kommentar</Typography>
                                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#555', cursor: 'copy' }}
                                            onClick={() => navigator.clipboard.writeText(claim.kommentar)}>
                                            {claim.kommentar}
                                        </Typography>
                                    </Box>
                                )}
                                <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#999', mb: 0.5 }}>Sendungsnummer(n)</Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {(claim.suchbegriffe || []).map((s, i) => (
                                        <Chip key={i} label={s} size="small" variant="outlined" clickable
                                            onClick={() => navigator.clipboard.writeText(s)}
                                            sx={{ cursor: 'copy', fontFamily: 'monospace', fontWeight: 600, borderColor: '#ccc' }} />
                                    ))}
                                </Stack>
                            </Box>
                            <ShipmentSearchTool posConfig={posConfig} onSelect={handleRechercheSelect} />
                        </Box>
                    ) : (
                        <Box>
                            <ComparisonView
                                claim={claim}
                                shipments={[rechercheResult]}
                                approvingId={actionLoading === 'rechercheApprove' ? rechercheResult.id : null}
                                done={false}
                                onApprove={() => isRechFremd ? setRechercheConfirmOpen(true) : executeRechercheApprove()}
                            />
                            {!isImLager && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>Nicht im Lager — Freigabe nicht möglich.</Alert>}
                            {isRechFremd && isImLager && <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }} icon={<WarningAmberIcon />}>Sendung gehört Kunde {rechercheResult.kdnr}.</Alert>}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    {rechercheResult && <Button onClick={() => setRechercheResult(null)} sx={{ mr: 'auto', textTransform: 'none', fontSize: 13 }}>← Zurück</Button>}
                    <Button onClick={() => setRechercheOpen(false)} sx={{ textTransform: 'none', fontSize: 13 }}>Abbrechen</Button>
                </DialogActions>
            </Dialog>

            {/* ── Fremdkunden-Bestätigung ── */}
            <Dialog open={rechercheConfirmOpen} onClose={() => setRechercheConfirmOpen(false)} PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ color: '#d97706', fontWeight: 700, fontSize: 16 }}>Sendung wird entzogen</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Sendung <strong>{rechercheResult?.id}</strong> wird Kunde <strong>{rechercheResult?.kdnr}</strong> entzogen
                        und Kunde <strong>{claim.kdnr}</strong> ({claim.kundenName || '–'}) zugewiesen.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setRechercheConfirmOpen(false)} sx={{ textTransform: 'none', fontSize: 13 }}>Abbrechen</Button>
                    <Button onClick={executeRechercheApprove} color="warning" variant="contained" disableElevation
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: 13 }}>Trotzdem freigeben</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ClaimCard;
